/**
 * Link — inline text link.
 *
 * Used inside body copy (consent paragraphs, helper text, T&C). For
 * button-shaped link affordances ("Already have an account?",
 * "adsensor.ru →"), use a `Button variant="ghost"` instead — Figma
 * implements those as Ghost Buttons, not as Link atoms.
 *
 * Style summary (matches Figma's inline consent links):
 *   - p12_400 / body.s by default (consent copy size)
 *   - primary/primary color (#3e88c5)
 *   - genuine `text-decoration: underline` (always on)
 *
 * Sizes accept the body. typography scale (s / m / l) so the link inherits
 * the text size of its surrounding paragraph.
 *
 * @format
 */

import {forwardRef, type AnchorHTMLAttributes} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {text} from 'ui/theme/typography';
import {
  useSurfaceContext,
  type SurfaceContext as SurfaceContextValue,
} from 'ui/theme/surfaceContext';

import {Icon} from '../Icon';

/**
 * Public enums for the controls. Stories import these arrays directly so
 * the control options auto-sync when an enum changes. `as const` keeps the
 * tuples narrow; the types below are derived from them.
 */
export const LINK_SIZES = ['s', 'm', 'l'] as const;
export const LINK_TONES = ['primary', 'neutral'] as const;

export type LinkSize = (typeof LINK_SIZES)[number];
export type LinkTone = (typeof LINK_TONES)[number];

export interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  /** Matches surrounding text size. Defaults to `s` (body.s — consent-copy size). */
  size?: LinkSize;
  /**
   * Colour role. `primary` is the standard blue link; `neutral` matches
   * surrounding text colour with an underline only.
   */
  tone?: LinkTone;
  /**
   * Adds `target="_blank" rel="noopener noreferrer"` and renders a small
   * `arrow_outward` icon after the label.
   */
  external?: boolean;
  /**
   * Surface-context override. By default Link consults the React
   * SurfaceContext (set by `<SurfaceContextProvider>` from a Modal panel,
   * an inverted page section, etc.). On a dark canvas links render
   * `primary.main`; on a light canvas (Modal body) they render
   * `primary.inverseMain` so the blue stays legible against the inverse
   * background. Pass an explicit value to override the inherited
   * context.
   */
  context?: SurfaceContextValue;
}

const Anchor = styled('a', {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$size: LinkSize; $tone: LinkTone}>(({$size, $tone, theme}) => {
  const role = $size === 'l' ? 'body.l' : $size === 'm' ? 'body.m' : 'body.s';
  const p = theme.palette;

  // Regular palette reads — SurfaceContextProvider sets
  // `data-color-scheme` on a wrapper (or the Link itself sets it on
  // its anchor when an explicit `context` prop is passed), so MUI's
  // CSS-var cascade resolves these to the right per-context values.
  const onSurface = p.text.primary;
  const main = p.primary.main;
  const hover = p.primary.hover;
  const active = p.primary.active;

  const color = $tone === 'primary' ? main : onSurface;
  const hoverColor = $tone === 'primary' ? hover : onSurface;
  const activeColor = $tone === 'primary' ? active : onSurface;

  return {
    ...text(role),
    color,
    // Underline lives on the inner label span (see below), NOT on the
    // anchor — otherwise the decoration is painted across the whole
    // inline-flex row and crosses the trailing `external` icon glyph.
    // `none` here is load-bearing: it overrides the browser UA default
    // (`a[href] { text-decoration: underline }`), which would otherwise
    // re-underline the whole row.
    textDecorationLine: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    cursor: 'pointer',
    transition: theme.transitions.create(['color'], {
      duration: theme.transitions.duration.shorter,
    }),

    '&:hover': {color: hoverColor},
    '&:active': {color: activeColor},
    '&:focus-visible': {
      outline: `2px solid ${main}`,
      outlineOffset: 2,
      borderRadius: 2,
    },
  };
});

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  {size = 's', tone = 'primary', external = false, context: contextProp, children, ...rest},
  ref,
) {
  const iconSize: 'xs' | 's' = size === 's' ? 'xs' : 's';
  const context = useSurfaceContext(contextProp);

  return (
    <Anchor
      ref={ref}
      data-color-scheme={context}
      $size={size}
      $tone={tone}
      target={external ? '_blank' : rest.target}
      rel={external ? 'noopener noreferrer' : rest.rel}
      {...rest}
    >
      {/*
       * Underline is scoped to the text label so the trailing `external`
       * icon (a sibling flex item) never gets crossed by the decoration.
       * `Box component='span'` keeps it valid phrasing content inside the
       * consuming <p> and satisfies the DS lint rules (no styled('span')).
       */}
      <Box
        component='span'
        style={{
          textDecorationLine: 'underline',
          textDecorationStyle: 'solid',
          textUnderlineOffset: '2px',
        }}
      >
        {children}
      </Box>
      {external && <Icon name='arrow_outward' size={iconSize} color='inherit' />}
    </Anchor>
  );
});
